import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { concat, EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CompanyDataService } from 'src/app/core/data-services/Company/company.data.service';
import { UserDataService } from 'src/app/core/data-services/identity/user.data.service';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { ClientInfoDto, LookupItemDto, UpdateCompanyInput } from 'src/app/core/data-models/Company-info.data.models';

@Component({
  selector: 'app-client-info',
  templateUrl: './client-info.component.html'
})
export class ClientInfoComponent implements OnInit, OnChanges {
  @Input() formData: ClientInfoDto | null = null;
  @Input() isDetails: boolean = true;
  @Input() isDisabled: boolean = true;
  /** When true (e.g. fund step 1), CR fields stay editable after load (no grey/disabled UI, dropdowns stay open). */
  @Input() allowEditAllFields = false;
  /** Lock each company field independently when it already has saved data (e.g. fund application step 1). */
  @Input() lockFilledFields = false;
  /** When true (e.g. supplier company profile), never show commercial registration status in the UI. */
  @Input() hideCrStatus = false;
  @Input() preloadedCompanyTypes: LookupItemDto[] | null = null;
  @Input() preloadedCompanyActivities: LookupItemDto[] | null = null;
  @Output() next = new EventEmitter<ClientInfoDto>();
  form!: FormGroup;
  companyTypes: LookupItemDto[] = [];
  companyActivities: LookupItemDto[] = [];
  lookupsLoading = true;
  /** Show CR status (حالة السجل التجاري) only when the API returns a non-empty value. */
  showCrStatus = false;
  /** Gregorian year navigator for establishment date */
  readonly crYearRange = `${new Date().getFullYear() - 200}:${new Date().getFullYear()}`;
 id!:string;
  constructor(
    private fb: FormBuilder,
    private clientInfoService: CompanyDataService,
    private userDataService: UserDataService,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.initForm();
    this.loadLookupData();
    if (this.formData) {
      this.patchClientInfo(this.formData);
    } else {
      this.loadClientInfo();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) return;
    if (changes['formData']?.currentValue) {
      this.patchClientInfo(changes['formData'].currentValue as ClientInfoDto);
    }
    if (changes['preloadedCompanyTypes'] || changes['preloadedCompanyActivities']) {
      this.applyPreloadedLookups();
    }
    if (changes['lockFilledFields']) {
      this.refreshFilledFieldLocks();
    }
  }

  private initForm() {
    this.showCrStatus = this.hasCrStatusFromApi(this.formData?.crStatus);

    this.form = this.fb.group({
        id: [{ value: this.formData?.id || '', disabled: this.isDisabled }],
      crNumber: [{ value: this.formData?.crNumber || '', disabled: this.isDisabled }, [Validators.required, Validators.pattern(/^\d{10}$/)]],
      crStatus: [
        { value: this.formData?.crStatus || '', disabled: true },
        [],
      ],
      unitedNationsNumber: [{ value: this.formData?.unitedNationsNumber || '', disabled: this.isDisabled }, [Validators.required, Validators.pattern(/^7\d{9}$/)]],
      companyName: [{ value: this.formData?.companyName || '', disabled: this.isDisabled }, Validators.required],
      commercialRegistrationExpiryDate: [{ value: this.formData?.commercialRegistrationExpiryDate || '', disabled: this.isDisabled }, Validators.required],
      companyTypeId: [{ value: this.formData?.companyTypeId || '', disabled: this.isDisabled }, Validators.required],
      companyActivityId: [{ value: this.formData?.companyActivityId || '', disabled: this.isDisabled }, Validators.required],
      establishmentDate: [this.formData?.establishmentDate || '', Validators.required],
      numberOfEmployees: [this.formData?.numberOfEmployees || '', [Validators.required, Validators.min(1)]],
      vatNumber: [this.formData?.vatNumber || ''],
      website: [this.formData?.website || '', [Validators.pattern('https?://.+')]]
    });
    if (this.isDisabled) {
      this.form.disable();
    }

    // CR status is display-only; never editable or submitted from frontend.
    this.form.get('crStatus')?.disable({ emitEvent: false });
  }

  // أضف هذه الدوال فقط ↓
  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (field?.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) return this.translate.instant('validation.required');
      if (field.errors['min']) return this.translate.instant('validation.minGreaterThanZero');
      if (field.errors['pattern']) {
        if (fieldName === 'crNumber') return this.translate.instant('validation.crNumberInvalid');
        if (fieldName === 'unitedNationsNumber') return this.translate.instant('validation.unitedNationsNumberInvalid');
        return this.translate.instant('validation.websiteInvalid');
      }
    }
    return '';
  }

  /** Load lookups one request after another to reduce simultaneous load on the server. */
  private loadLookupData() {
    this.applyPreloadedLookups();

    const typeFromForm = (this.formData?.companyTypeId ?? '').toString();
    const activityFromForm = (this.formData?.companyActivityId ?? '').toString();

    const hasTypeInPreloaded =
      !!typeFromForm &&
      this.companyTypes.some((x) => String(x?.id ?? '') === typeFromForm);
    const hasActivityInPreloaded =
      !!activityFromForm &&
      this.companyActivities.some((x) => String(x?.id ?? '') === activityFromForm);

    const shouldLoadTypes = this.companyTypes.length === 0 || !hasTypeInPreloaded;
    const shouldLoadActivities = this.companyActivities.length === 0 || !hasActivityInPreloaded;

    if (!shouldLoadTypes && !shouldLoadActivities) {
      this.lookupsLoading = false;
      return;
    }

    this.lookupsLoading = true;
    const normalize = (items: any[] | null | undefined): LookupItemDto[] =>
      (items || []).map((x: any) => ({
        ...x,
        name: (x?.name || x?.nameAr || x?.nameEn || '').toString(),
      }));

    const types$ = this.clientInfoService.GetCompanyTypes().pipe(
      tap((res) => {
        if (!shouldLoadTypes) return;
        this.companyTypes = normalize((res?.success && res.resource ? res.resource : []) as any[]);
      }),
      catchError(() => {
        if (shouldLoadTypes) this.companyTypes = [];
        this.notificationService.error('Error loading company types:');
        return EMPTY;
      })
    );

    const activities$ = this.clientInfoService.GetCompanyActivities().pipe(
      tap((res) => {
        if (!shouldLoadActivities) return;
        this.companyActivities = this.normalizeCompanyActivities(
          (res?.success && res.resource ? res.resource : []) as any[]
        );
      }),
      catchError(() => {
        if (shouldLoadActivities) this.companyActivities = [];
        this.notificationService.error('Error loading company activities:');
        return EMPTY;
      })
    );

    if (shouldLoadTypes && shouldLoadActivities) {
      concat(types$, activities$)
        .pipe(finalize(() => (this.lookupsLoading = false)))
        .subscribe();
      return;
    }

    const single$ = shouldLoadTypes ? types$ : activities$;
    single$
      .pipe(finalize(() => (this.lookupsLoading = false)))
      .subscribe();
  }

  private applyPreloadedLookups(): void {
    if (Array.isArray(this.preloadedCompanyTypes) && this.preloadedCompanyTypes.length > 0) {
      this.companyTypes = this.preloadedCompanyTypes;
    }

    if (Array.isArray(this.preloadedCompanyActivities) && this.preloadedCompanyActivities.length > 0) {
      this.companyActivities = this.preloadedCompanyActivities;
    }
  }

  /** Same shape as register sector list: searchable `name`, optional `nameAr` / `nameEn` for two-line UI. */
  private normalizeCompanyActivities(items: any[] | null | undefined): LookupItemDto[] {
    return (items || []).map((x: any) => {
      const id = x?.id != null ? x.id : x?.Id;
      const nameAr = String(x?.nameAr ?? x?.NameAr ?? '').trim();
      const nameEn = String(x?.nameEn ?? x?.NameEn ?? '').trim();
      const fallback = String(x?.name ?? x?.Name ?? '').trim();
      const filterParts = [nameAr, nameEn, fallback].filter((s, i, a) => s && a.indexOf(s) === i);
      const name = filterParts.join(' ') || fallback;
      return { ...x, id, name, nameAr, nameEn } as LookupItemDto;
    });
  }

  /** First line: Arabic preferred, else English, else combined label / filter name. */
  activityPrimary(item: LookupItemDto | null | undefined): string {
    if (!item) return '';
    const any = item as any;
    const ar = String(any.nameAr ?? '').trim();
    const en = String(any.nameEn ?? '').trim();
    const fb = String(item.name ?? '').trim();
    if (ar) return ar;
    if (en) return en;
    return fb;
  }

  /** Second line: English when Arabic is on the first line and differs. */
  activitySecondary(item: LookupItemDto | null | undefined): string {
    if (!item) return '';
    const any = item as any;
    const ar = String(any.nameAr ?? '').trim();
    const en = String(any.nameEn ?? '').trim();
    if (ar && en && en !== ar) return en;
    return '';
  }

  private loadClientInfo() {
    this.clientInfoService.GetClientInfoByCrNumber().subscribe({
      next: (res) => {
        if (res?.success && res.resource) {
          this.patchClientInfo(res.resource);
        }
      },
      error: (err) => {
        this.notificationService.error('Error loading client info:');
      }
    });
  }

  private patchClientInfo(data: ClientInfoDto): void {
    this.form.patchValue(data);
    this.syncCrStatusVisibility(data);
    this.refreshFilledFieldLocks();
  }

  /** Fields that lock after save when `lockFilledFields` is on. Number of employees stays editable (supplier/borrower may correct count). */
  private readonly lockableFilledFieldNames = [
    'crNumber',
    'unitedNationsNumber',
    'companyName',
    'commercialRegistrationExpiryDate',
    'companyTypeId',
    'companyActivityId',
    'establishmentDate',
    'vatNumber',
    'website',
  ] as const;

  private refreshFilledFieldLocks(): void {
    if (!this.form || !this.lockFilledFields || this.isDisabled) return;

    for (const name of this.lockableFilledFieldNames) {
      const c = this.form.get(name);
      if (!c) continue;

      const filled = this.hasPersistedCompanyValue(name, c.value);
      if (filled) {
        c.disable({ emitEvent: false });
      } else {
        c.enable({ emitEvent: false });
      }
    }

    // CR status stays read-only regardless
    this.form.get('crStatus')?.disable({ emitEvent: false });
  }

  private hasPersistedCompanyValue(controlName: string, value: unknown): boolean {
    if (value === null || value === undefined) return false;
    switch (controlName) {
      case 'numberOfEmployees': {
        if (typeof value === 'number') return !isNaN(value) && value > 0;
        const n = parseInt(String(value), 10);
        return !isNaN(n) && n > 0;
      }
      case 'establishmentDate':
      case 'commercialRegistrationExpiryDate':
        if (value instanceof Date) return !isNaN(value.getTime());
        if (typeof value === 'string') return value.trim().length > 0;
        return !!value;
      case 'companyTypeId':
      case 'companyActivityId':
        if (value === '' || value === null || value === undefined) return false;
        if (typeof value === 'number') return !isNaN(value);
        return String(value).trim().length > 0;
      default:
        return String(value).trim().length > 0;
    }
  }

  private hasCrStatusFromApi(value: unknown): boolean {
    return value != null && String(value).trim() !== '';
  }

  /** Hide CR status when API does not supply it; do not require an empty hidden field. */
  private syncCrStatusVisibility(data: ClientInfoDto | null | undefined): void {
    const ctrl = this.form?.get('crStatus');
    if (!ctrl) return;
    const show = this.hasCrStatusFromApi(data?.crStatus);
    this.showCrStatus = show;
    if (show) {
      ctrl.clearValidators();
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.disable({ emitEvent: false });
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  onCrNumberInput(): void {
    const c = this.form.get('crNumber');
    if (!c) return;
    let v = (c.value ?? '').toString().replace(/\D/g, '');
    if (v.length > 10) v = v.slice(0, 10);
    c.setValue(v, { emitEvent: false });
  }

  onUnitedNationsNumberInput(): void {
    const c = this.form.get('unitedNationsNumber');
    if (!c) return;
    let v = (c.value ?? '').toString().replace(/\D/g, '');
    if (!v) {
      c.setValue('', { emitEvent: false });
      return;
    }
    if (v.length === 1 && v !== '7') v = '7';
    if (!v.startsWith('7')) v = `7${v.substring(1)}`;
    if (v.length > 10) v = v.slice(0, 10);
    c.setValue(v, { emitEvent: false });
  }

  getFormData() {
    if (this.form.valid) return this.form.value as ClientInfoDto;
    this.form.markAllAsTouched();
    return null;
  }
     save() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const dto = this.form.getRawValue() as UpdateCompanyInput;

    this.userDataService.EditCompantDetails(dto).subscribe({
      next: (res) => {
        if (res?.success) {
         //notification can be added here
         this.loadClientInfo(); // Reload to get updated data
        }
      },
      error: (err) => console.error("Error saving client info:", err)
    });
  }
}
